import React, { useState, useEffect } from "react";
import { useTenant } from "@/store/tenant";
import type { Tenant } from "@/types";
import { Key, Copy, RefreshCw, AlertTriangle, Unlock, Upload, FileCheck, X, Download } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { toast } from "sonner";

interface TenantKeysTabProps {
  tenant: Tenant;
}

export const TenantKeysTab: React.FC<TenantKeysTabProps> = ({ tenant }) => {
  const tenantStore = useTenant(tenant.id);
  const [pgpPublicKey, setPgpPublicKey] = useState<string>("");
  const [isFetchingKey, setIsFetchingKey] = useState(false);
  const [isRegeneratingKey, setIsRegeneratingKey] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Decrypt states
  const [decryptMode, setDecryptMode] = useState<"text" | "file">("text");
  const [encryptedInput, setEncryptedInput] = useState("");
  const [decryptedOutput, setDecryptedOutput] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);

  // Decrypted file states
  const [decryptedFileBlob, setDecryptedFileBlob] = useState<Blob | null>(null);
  const [decryptedFileName, setDecryptedFileName] = useState("");
  const [decryptedFileText, setDecryptedFileText] = useState<string | null>(null);

  // Clear file decryption output when file or mode changes
  useEffect(() => {
    setDecryptedFileBlob(null);
    setDecryptedFileName("");
    setDecryptedFileText(null);
  }, [selectedFile, decryptMode]);

  const handleDecrypt = async () => {
    if (!encryptedInput.trim()) {
      toast.error("Please enter an encrypted message to decrypt");
      return;
    }
    setIsDecrypting(true);
    setDecryptedOutput("");
    try {
      const result = await tenantStore.decryptPgpMessage(encryptedInput);
      setDecryptedOutput(result);
      toast.success("Message decrypted successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to decrypt message. Make sure the message was encrypted with this tenant's current public key.");
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleDecryptFile = async () => {
    if (!selectedFile) {
      toast.error("Please select a PGP encrypted file to decrypt");
      return;
    }
    setIsDecrypting(true);
    try {
      const blob = await tenantStore.decryptPgpFile(selectedFile);
      setDecryptedFileBlob(blob);

      const origName = selectedFile.name;
      let outName = "decrypted.bin";
      if (origName.endsWith(".gpg")) {
        outName = origName.slice(0, -4);
      } else if (origName.endsWith(".pgp")) {
        outName = origName.slice(0, -4);
      } else if (origName.endsWith(".asc")) {
        outName = origName.slice(0, -4);
      } else {
        outName = `decrypted_${origName}`;
      }
      setDecryptedFileName(outName);

      // Attempt to parse/read as text to decide display option
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text === "string") {
          if (text.includes("\u0000")) {
            setDecryptedFileText(null); // binary/non-text file
          } else {
            setDecryptedFileText(text);
          }
        } else {
          setDecryptedFileText(null);
        }
      };
      reader.onerror = () => {
        setDecryptedFileText(null);
      };
      reader.readAsText(blob);

      toast.success("File decrypted successfully");
    } catch (err: any) {
      // Error message already handled inside the store
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleDownloadDecryptedFile = () => {
    if (!decryptedFileBlob) return;
    const url = window.URL.createObjectURL(decryptedFileBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", decryptedFileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("File download started");
  };

  useEffect(() => {
    if (tenant.id) {
      setIsFetchingKey(true);
      tenantStore.getPgpPublicKey()
        .then(key => setPgpPublicKey(key))
        .catch(err => console.error(err))
        .finally(() => setIsFetchingKey(false));
    }
  }, [tenant.id]);

  const handleRegenerateKeys = async () => {
    setConfirmOpen(false);
    setIsRegeneratingKey(true);
    try {
      await tenantStore.generatePgpKeypair();
      const key = await tenantStore.getPgpPublicKey();
      setPgpPublicKey(key);
      toast.success("PGP Keypair regenerated successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to regenerate PGP keypair");
    } finally {
      setIsRegeneratingKey(false);
    }
  };

  const handleCopyKey = () => {
    if (!pgpPublicKey) return;
    navigator.clipboard.writeText(pgpPublicKey);
    toast.success("Public key copied to clipboard");
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-350">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] border border-slate-200/60 dark:border-slate-800/60 p-6 space-y-6">
        <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800/60 pb-4">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-orange-500" />
            <h3 className="text-[15px] font-bold text-slate-900 dark:text-white">
              PGP Encryption Key
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {pgpPublicKey && (
              <Button
                variant="ghost"
                onClick={handleCopyKey}
                className="text-xs py-1.5 px-3 h-auto rounded-full font-semibold flex items-center gap-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy Public Key
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              disabled={isRegeneratingKey}
              className="text-xs py-1.5 px-4 h-auto rounded-full font-semibold flex items-center gap-1.5 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white dark:border-orange-500/50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRegeneratingKey ? "animate-spin" : ""}`} />
              {isRegeneratingKey ? "Regenerating..." : "Regenerate Keys"}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
            Public Key (PEM format)
          </label>
          {isFetchingKey ? (
            <div className="h-32 flex items-center justify-center border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-950/20">
              <span className="text-xs text-slate-500 animate-pulse">Loading public key...</span>
            </div>
          ) : pgpPublicKey ? (
            <textarea
              readOnly
              value={pgpPublicKey}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              className="w-full h-64 text-xs font-mono p-4 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-955/20 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-orange-500/50 resize-none cursor-pointer"
            />
          ) : (
            <div className="h-32 flex flex-col items-center justify-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/10">
              <span className="text-xs text-slate-500">No PGP key pair has been generated yet for this tenant.</span>
              <Button
                variant="primary"
                onClick={() => setConfirmOpen(true)}
                className="mt-2 text-xs py-1 px-3 h-auto rounded-full font-semibold"
              >
                Generate Keypair
              </Button>
            </div>
          )}
          <p className="text-[10px] text-slate-500 leading-normal">
            Clients can use this public key to encrypt secrets before uploading them to the platform.
            PGP encrypted fields should be prefixed with <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[9px] font-bold">pgp-</code> (e.g. <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[9px]">pgp-hQIMA8...</code>) so the platform decrypts them automatically.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] border border-slate-200/60 dark:border-slate-800/60 p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-b border-slate-100 dark:border-slate-800/60 pb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-orange-500" />
              <h3 className="text-[15px] font-bold text-slate-900 dark:text-white">
                Decrypt PGP
              </h3>
            </div>
            <div className="flex bg-slate-100 dark:bg-slate-800/80 p-0.5 rounded-full border border-slate-200/40 dark:border-slate-700/40">
              <button
                type="button"
                onClick={() => setDecryptMode("text")}
                className={`text-[10px] uppercase tracking-wider py-1 px-3 rounded-full font-bold transition-all ${decryptMode === "text"
                  ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  }`}
              >
                Text
              </button>
              <button
                type="button"
                onClick={() => setDecryptMode("file")}
                className={`text-[10px] uppercase tracking-wider py-1 px-3 rounded-full font-bold transition-all ${decryptMode === "file"
                  ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  }`}
              >
                File
              </button>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={decryptMode === "text" ? handleDecrypt : handleDecryptFile}
            disabled={
              isDecrypting ||
              (decryptMode === "text" ? !encryptedInput.trim() : !selectedFile)
            }
            className="text-xs py-1.5 px-4 h-auto rounded-full font-semibold flex items-center gap-1.5 self-end sm:self-auto"
          >
            {isDecrypting ? "Decrypting..." : "Decrypt"}
          </Button>
        </div>

        {decryptMode === "text" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-200">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                Encrypted Message Input
              </label>
              <textarea
                value={encryptedInput}
                onChange={(e) => setEncryptedInput(e.target.value)}
                placeholder="Paste PGP encrypted text here (optionally with 'pgp-' prefix)..."
                className="w-full h-48 text-xs font-mono p-4 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-955/20 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-orange-500/50 resize-none"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                Decrypted Message Output
              </label>
              <textarea
                readOnly
                value={decryptedOutput}
                placeholder="Decrypted plain text will appear here..."
                className="w-full h-48 text-xs font-mono p-4 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-955/20 text-slate-850 dark:text-slate-200 focus:outline-none resize-none"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-200">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                Encrypted File Input
              </label>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    setSelectedFile(e.dataTransfer.files[0]);
                  }
                }}
                className={`h-48 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center gap-3 transition-all duration-200 bg-slate-50/50 dark:bg-slate-950/20 ${isDragOver
                  ? "border-orange-500 bg-orange-50/30 dark:bg-orange-950/10"
                  : selectedFile
                    ? "border-primary-500 dark:border-emerald-500/50"
                    : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                  }`}
              >
                <input
                  type="file"
                  id="file-decrypt-input"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                />
                {selectedFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl flex items-center justify-center text-emerald-500">
                      <FileCheck className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 max-w-[250px] truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="text-[10px] font-bold text-rose-500 hover:text-rose-600 flex items-center gap-1 mt-1 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Clear File
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="file-decrypt-input"
                    className="cursor-pointer flex flex-col items-center gap-2 text-center px-4"
                  >
                    <div className="w-12 h-12 bg-orange-50 dark:bg-orange-950/30 rounded-xl flex items-center justify-center text-orange-500 transition-transform hover:scale-105 duration-200">
                      <Upload className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 hover:underline">
                        Click to upload
                      </span>
                      <span className="text-xs text-slate-500"> or drag and drop</span>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Supports encrypted files (e.g. .gpg, .pgp, .asc)
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            <div className="space-y-2 flex flex-col h-full">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                Decrypted File Output
              </label>

              <div className="relative w-full h-48 flex-1">
                {decryptedFileBlob && (
                  <button
                    type="button"
                    onClick={handleDownloadDecryptedFile}
                    className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-650 hover:text-slate-900 dark:text-slate-350 dark:hover:text-white transition-all shadow-sm flex items-center justify-center"
                    title="Download decrypted file"
                  >
                    <Download className="w-4 h-4 text-orange-500" />
                  </button>
                )}

                {decryptedFileBlob ? (
                  decryptedFileText !== null ? (
                    <textarea
                      readOnly
                      value={decryptedFileText}
                      placeholder="Decrypted file content..."
                      className="w-full h-full text-xs font-mono p-4 pr-14 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-955/20 text-slate-850 dark:text-slate-200 focus:outline-none resize-none"
                    />
                  ) : (
                    <div className="w-full h-full border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-955/20 flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl flex items-center justify-center text-emerald-500 mb-2">
                        <FileCheck className="w-6 h-6" />
                      </div>
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        Decrypted Binary File
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1 max-w-[200px] truncate">
                        {decryptedFileName}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {(decryptedFileBlob.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  )
                ) : (
                  <div className="w-full h-full border border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/10 flex items-center justify-center p-4">
                    <span className="text-xs text-slate-500">
                      Decrypted file content will appear here...
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Confirmation Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-all animate-in fade-in">
          <Card
            className="max-w-md w-full shadow-2xl scale-in-center overflow-hidden"
            title="Regenerate PGP Keypair"
            subtitle="Are you absolutely sure?"
          >
            <div className="space-y-6">
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-xs font-bold text-amber-800 dark:text-amber-300">
                    Important Warning
                  </h4>
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-normal">
                    Regenerating keys will invalidate the current active public/private key pair. Any existing configuration fields encrypted using the old key will fail to decrypt and must be re-encrypted and re-uploaded.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleRegenerateKeys}
                >
                  Regenerate Keys
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
